interface MediaLike {
  id: string;
  url: string;
  /** Stored file name, from the API (`ProductMedia.fileName`). */
  fileName?: string | null;
  originalUrl?: string;
  oembedData?: string | null;
}

interface VariantLike {
  id: string;
  media?: Array<{ id: string }> | null;
}

/** Numeric sort-order comparator; nulls last (as Postgres sorts them). */
export const bySortOrder = (
  a: { sortOrder?: number | null },
  b: { sortOrder?: number | null },
): number => (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity) || 0;

/** File name of a media item as stored (e.g. "F250-4100-541-004-20_1.jpg"). */
export const mediaFilename = (media: MediaLike): string => {
  if (media.fileName) {
    return media.fileName;
  }

  try {
    const oembed = media.oembedData ? JSON.parse(media.oembedData) : null;

    if (oembed?.title) {
      // External media (video URL) has no stored file — show its title.
      return oembed.title;
    }
  } catch {
    // Malformed oembed data — fall through to the url.
  }

  // Fallback for media without a file name (e.g. an upload still in flight).
  const source = media.originalUrl || media.url || "";
  const basename = source.split("?")[0].split("/").filter(Boolean).pop() ?? "";

  try {
    return decodeURIComponent(basename);
  } catch {
    return basename;
  }
};

/**
 * The images the storefront gallery shows, in display order.
 *
 * Mirrors the storefront rule (redparts `services/product-media.ts`):
 *  - no variant selected → every product image;
 *  - variant selected → shared images (assigned to no variant) plus that
 *    variant's own images, interleaved by the product's sort order;
 *  - if that leaves nothing (no shared images, variant has none) → every image.
 */
export const storefrontGallery = <M extends MediaLike>(
  orderedMedia: M[],
  variants: VariantLike[],
  variantId: string | null,
): M[] => {
  if (!variantId) {
    return orderedMedia;
  }

  const assigned = new Set(variants.flatMap(variant => (variant.media ?? []).map(({ id }) => id)));
  const own = new Set(
    (variants.find(variant => variant.id === variantId)?.media ?? []).map(({ id }) => id),
  );
  const shown = orderedMedia.filter(media => !assigned.has(media.id) || own.has(media.id));

  return shown.length ? shown : orderedMedia;
};
